import { useMemo, useState } from 'react';
import { Box, Flex, HStack, Input, Text, VStack } from '@chakra-ui/react';
import moment from 'moment';
import { Icon, IconButton, Tooltip, checkboxMenuTriggerMetrics } from '../../ui';
import type { IconName } from '../../ui';
import { AskCarlThread } from '../../types/askCarl';

/**
 * Carl page sidebar (ENG-2169) — Vanta-style conversation rail.
 *
 * Expanded: "Carl AI" header with a collapse control, New chat / Search chats
 * rows, and a collapsible "Recent chats" section listing the full thread
 * history (rename + delete on hover). Collapsed: an icon-only rail with
 * tooltips. The expanded/collapsed choice persists per browser.
 *
 * Type runs on the design system's menu scale (13px rows, 10px section
 * label) so the rail reads as chrome, not content.
 */

/** localStorage keys (page-level UI prefs, not per-user data). */
export const SIDEBAR_COLLAPSED_KEY = 'ask-carl:sidebar-collapsed';
const RECENTS_COLLAPSED_KEY = 'ask-carl:recents-collapsed';

const SIDEBAR_WIDTH = '244px';
const SIDEBAR_RAIL_WIDTH = '52px';

/** Menu-scale type shared by every rail row (13px — same as yaxle menus). */
const ROW_FONT_SIZE = checkboxMenuTriggerMetrics.fontSize;

export const readStoredFlag = (key: string): boolean => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(key) === '1';
};

export const writeStoredFlag = (key: string, value: boolean) => {
    if (typeof window === 'undefined') return;
    if (value) window.localStorage.setItem(key, '1');
    else window.localStorage.removeItem(key);
};

interface CarlSidebarProps {
    threads: AskCarlThread[];
    activeThreadId: string | null;
    isCollapsed: boolean;
    onToggleCollapsed: () => void;
    onNewChat: () => void;
    onOpenSearch: () => void;
    onSelectThread: (threadId: string) => void;
    onDeleteThread: (threadId: string) => void;
    onRenameThread: (threadId: string, title: string) => void;
}

/** Expanded-rail action row (New chat / Search chats). */
const SidebarActionRow = ({
    icon,
    label,
    onClick,
}: {
    icon: IconName;
    label: string;
    onClick: () => void;
}) => (
    <Flex
        as="button"
        type="button"
        align="center"
        gap={2.5}
        w="100%"
        px={2.5}
        py={2}
        borderRadius="md"
        cursor="pointer"
        transition="background 0.12s"
        _hover={{ bg: 'bg.subtle' }}
        onClick={onClick}
    >
        <Icon name={icon} size="sm" color="fg.muted" />
        <Text fontSize={ROW_FONT_SIZE} color="fg.default">
            {label}
        </Text>
    </Flex>
);

/** One conversation row: select on click, rename/delete revealed on hover. */
const SidebarThreadRow = ({
    thread,
    isActive,
    onSelect,
    onDelete,
    onRename,
}: {
    thread: AskCarlThread;
    isActive: boolean;
    onSelect: (threadId: string) => void;
    onDelete: (threadId: string) => void;
    onRename: (threadId: string, title: string) => void;
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [draftTitle, setDraftTitle] = useState(thread.title);

    const commitRename = () => {
        setIsEditing(false);
        if (draftTitle.trim() && draftTitle.trim() !== thread.title) {
            onRename(thread.threadId, draftTitle);
        }
    };

    return (
        <Flex
            role="group"
            align="center"
            gap={1}
            px={2.5}
            py={1.5}
            minH="30px"
            borderRadius="md"
            cursor="pointer"
            bg={isActive ? 'brand.50' : 'transparent'}
            _hover={{ bg: isActive ? 'brand.50' : 'bg.subtle' }}
            transition="background 0.12s"
            onClick={() => {
                if (!isEditing) onSelect(thread.threadId);
            }}
        >
            {isEditing ? (
                <Input
                    value={draftTitle}
                    onChange={(event) => setDraftTitle(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') commitRename();
                        if (event.key === 'Escape') {
                            setDraftTitle(thread.title);
                            setIsEditing(false);
                        }
                    }}
                    onBlur={commitRename}
                    onClick={(event) => event.stopPropagation()}
                    size="xs"
                    autoFocus
                    bg="white"
                    borderColor="brand.solid"
                    borderRadius="md"
                    flex={1}
                />
            ) : (
                <Text
                    flex={1}
                    minW={0}
                    fontSize={ROW_FONT_SIZE}
                    color="fg.default"
                    fontWeight={isActive ? 'semibold' : 'normal'}
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                >
                    {thread.title}
                </Text>
            )}
            {!isEditing && (
                <HStack spacing={0} flexShrink={0}>
                    <IconButton
                        aria-label={`Rename ${thread.title}`}
                        icon={<Icon name="pencil" size="xs" />}
                        size="xs"
                        minW="20px"
                        h="20px"
                        variant="ghostGray"
                        opacity={0}
                        _groupHover={{ opacity: 1 }}
                        _focusVisible={{ opacity: 1 }}
                        onClick={(event) => {
                            event.stopPropagation();
                            setDraftTitle(thread.title);
                            setIsEditing(true);
                        }}
                    />
                    <IconButton
                        aria-label={`Delete ${thread.title}`}
                        icon={<Icon name="trash" size="xs" />}
                        size="xs"
                        minW="20px"
                        h="20px"
                        variant="ghostGray"
                        colorScheme="red"
                        opacity={0}
                        _groupHover={{ opacity: 1 }}
                        _focusVisible={{ opacity: 1 }}
                        onClick={(event) => {
                            event.stopPropagation();
                            onDelete(thread.threadId);
                        }}
                    />
                </HStack>
            )}
        </Flex>
    );
};

const CarlSidebar = ({
    threads,
    activeThreadId,
    isCollapsed,
    onToggleCollapsed,
    onNewChat,
    onOpenSearch,
    onSelectThread,
    onDeleteThread,
    onRenameThread,
}: CarlSidebarProps) => {
    const [recentsCollapsed, setRecentsCollapsed] = useState(() => readStoredFlag(RECENTS_COLLAPSED_KEY));

    const sortedThreads = useMemo(
        () => [...threads].sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '')),
        [threads],
    );

    const toggleRecents = () => {
        setRecentsCollapsed((previous) => {
            writeStoredFlag(RECENTS_COLLAPSED_KEY, !previous);
            return !previous;
        });
    };

    if (isCollapsed) {
        return (
            <Flex
                direction="column"
                align="center"
                w={SIDEBAR_RAIL_WIDTH}
                flexShrink={0}
                h="100%"
                bg="white"
                borderRight="1px solid"
                borderColor="border.default"
                pt={3.5}
                pb={3}
                gap={1.5}
                transition="width 0.2s ease"
            >
                <Tooltip label="Expand sidebar" placement="right">
                    <IconButton
                        aria-label="Expand sidebar"
                        icon={<Icon name="sidebarExpand" size="md" />}
                        size="sm"
                        variant="ghostGray"
                        onClick={onToggleCollapsed}
                    />
                </Tooltip>
                <Tooltip label="New chat" placement="right">
                    <IconButton
                        aria-label="New chat"
                        icon={<Icon name="edit" size="md" />}
                        size="sm"
                        variant="ghostGray"
                        onClick={onNewChat}
                    />
                </Tooltip>
                <Tooltip label="Search chats" placement="right">
                    <IconButton
                        aria-label="Search chats"
                        icon={<Icon name="search" size="md" />}
                        size="sm"
                        variant="ghostGray"
                        onClick={onOpenSearch}
                    />
                </Tooltip>
            </Flex>
        );
    }

    return (
        <Flex
            direction="column"
            w={SIDEBAR_WIDTH}
            flexShrink={0}
            h="100%"
            bg="white"
            borderRight="1px solid"
            borderColor="border.default"
            transition="width 0.2s ease"
        >
            {/* Header: page identity + collapse control */}
            <Flex align="center" justify="space-between" pl={4} pr={2} pt={3} pb={1}>
                <Text textStyle="h5" fontSize="sm" color="fg.default">
                    Carl AI
                </Text>
                <Tooltip label="Collapse sidebar" placement="right">
                    <IconButton
                        aria-label="Collapse sidebar"
                        icon={<Icon name="sidebarCollapse" size="md" />}
                        size="sm"
                        variant="ghostGray"
                        onClick={onToggleCollapsed}
                    />
                </Tooltip>
            </Flex>

            {/* Primary actions */}
            <VStack spacing={0.5} align="stretch" px={2}>
                <SidebarActionRow icon="edit" label="New chat" onClick={onNewChat} />
                <SidebarActionRow icon="search" label="Search chats" onClick={onOpenSearch} />
            </VStack>

            {/* Recent chats (collapsible) — label on the menu-label scale */}
            <Flex
                as="button"
                type="button"
                align="center"
                gap={1}
                px={4}
                pt={5}
                pb={1.5}
                onClick={toggleRecents}
                cursor="pointer"
                _focus={{ outline: 'none', boxShadow: 'none' }}
                _focusVisible={{ boxShadow: 'outline' }}
            >
                <Text
                    fontSize="0.625rem"
                    fontWeight="semibold"
                    color="fg.muted"
                    letterSpacing="0.07em"
                    textTransform="uppercase"
                >
                    Recent chats
                </Text>
                <Icon
                    name="chevronDown"
                    size="xs"
                    color="fg.muted"
                    transform={recentsCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)'}
                    transition="transform 0.15s ease"
                />
            </Flex>
            {!recentsCollapsed && (
                <Box flex={1} minH={0} overflowY="auto" px={2} pb={3}>
                    {sortedThreads.length === 0 ? (
                        <Text fontSize={ROW_FONT_SIZE} color="fg.muted" px={2.5} py={1.5}>
                            No conversations yet.
                        </Text>
                    ) : (
                        <VStack spacing={0.5} align="stretch">
                            {sortedThreads.map((thread) => (
                                <SidebarThreadRow
                                    key={thread.threadId}
                                    thread={thread}
                                    isActive={thread.threadId === activeThreadId}
                                    onSelect={onSelectThread}
                                    onDelete={onDeleteThread}
                                    onRename={onRenameThread}
                                />
                            ))}
                        </VStack>
                    )}
                </Box>
            )}
        </Flex>
    );
};

/** Compact age: now, 5m, 3h, 1d, 2w, 3mo — shared with the search modal. */
export const shortThreadAge = (timestamp: string): string => {
    const minutes = moment().diff(moment(timestamp), 'minutes');
    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 14) return `${days}d`;
    const weeks = Math.floor(days / 7);
    if (weeks < 9) return `${weeks}w`;
    return `${Math.floor(days / 30)}mo`;
};

export default CarlSidebar;
